import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import firstLeadSparkIcon from 'app/assets/leads/first-lead-spark.svg';
import { LeadsAccentButton } from 'app/features/leads/components/leads-pill-button';
import Typography from 'app/ui/components/atoms/typography';

type FirstLeadGuidanceProps = {
  onDismiss: () => void;
};

const Card = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme: { spacing } }) => spacing(6)};
  padding: ${({ theme: { spacing } }) => spacing(6)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(6)};
  border: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_200};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  background: ${({ theme }) => theme.semantic.neutrals_100};
  overflow: hidden;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      flex-direction: row;
    `,
    )}
`;

const IconFrame = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ theme: { spacing } }) => spacing(22)};
  height: ${({ theme: { spacing } }) => spacing(22)};
  border-radius: 50%;
  background: ${({ theme }) => theme.semantic.neutrals_50};
`;

const IconImage = styled.img`
  display: block;
  width: ${({ theme: { spacing } }) => spacing(16)};
  height: ${({ theme: { spacing } }) => spacing(10.5)};
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  min-width: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  padding: 0 ${({ theme: { spacing } }) => spacing(0.5)};
`;

const Description = styled(Typography)`
  font-size: ${({ theme: { typography } }) => typography.tiny};

  ${({ theme: { media, typography } }) =>
    media.medium(
      () => `
      font-size: ${typography.xsmall};
    `,
    )}
`;

const StepsList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(2.5)};
`;

const StepItem = styled.li`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  align-items: flex-start;
`;

/** HYROX dark (20535:2555): accent/100 fill, accent/500 ring + numeral */
const StepBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ theme: { spacing } }) => spacing(6)};
  height: ${({ theme: { spacing } }) => spacing(6)};
  border: 0.0625rem solid ${({ theme }) => theme.semantic.accent_500};
  border-radius: 50%;
  background: ${({ theme }) => theme.semantic.accent_100};
  color: ${({ theme }) => theme.semantic.accent_500};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.tiny};
  font-weight: ${({ theme: { typography } }) => typography.bold};
  line-height: 1;
`;

const StepCopy = styled.p`
  margin: 0;
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.xsmall};
  line-height: ${({ theme: { typography } }) => typography.lineHeight};
`;

const StepTitle = styled.span`
  font-weight: ${({ theme: { typography } }) => typography.bold};
  color: ${({ theme }) => theme.semantic.neutrals_950};
`;

const StepBody = styled.span`
  font-weight: ${({ theme: { typography } }) => typography.light};
  color: ${({ theme }) => theme.semantic.neutrals_700};
`;

const CtaButton = styled(LeadsAccentButton)`
  align-self: stretch;
  min-height: ${({ theme: { spacing } }) => spacing(8)};
  padding: ${({ theme: { spacing } }) => `${spacing(1.5)} ${spacing(2)}`};

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      align-self: flex-start;
      min-height: ${spacing(9)};
    `,
    )}
`;

export const FirstLeadGuidance = ({ onDismiss }: FirstLeadGuidanceProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();

  const steps = [
    {
      title: intl.formatMessage({ id: 'leads.firstLead.steps.review.title' }),
      body: intl.formatMessage({ id: 'leads.firstLead.steps.review.body' }),
    },
    {
      title: intl.formatMessage({ id: 'leads.firstLead.steps.respond.title' }),
      body: intl.formatMessage({ id: 'leads.firstLead.steps.respond.body' }),
    },
    {
      title: intl.formatMessage({ id: 'leads.firstLead.steps.data.title' }),
      body: intl.formatMessage({ id: 'leads.firstLead.steps.data.body' }),
    },
  ];

  return (
    <Card data-testid="leads-first-lead-guidance">
      <IconFrame aria-hidden="true">
        <IconImage src={firstLeadSparkIcon} alt="" />
      </IconFrame>
      <Content>
        <TitleBlock>
          <Typography variant="h4" color={semantic.text_small_elements}>
            {intl.formatMessage({ id: 'leads.firstLead.title' })}
          </Typography>
          <Description variant="body2" color={semantic.neutrals_700}>
            {intl.formatMessage({ id: 'leads.firstLead.description' })}
          </Description>
        </TitleBlock>
        <StepsList>
          {steps.map((step, index) => (
            <StepItem key={step.title}>
              <StepBadge>{index + 1}</StepBadge>
              <StepCopy>
                <StepTitle>{step.title}</StepTitle>
                <StepBody>{` — ${step.body}`}</StepBody>
              </StepCopy>
            </StepItem>
          ))}
        </StepsList>
        <CtaButton type="button" onClick={onDismiss}>
          {intl.formatMessage({ id: 'leads.firstLead.cta' })}
        </CtaButton>
      </Content>
    </Card>
  );
};

export default FirstLeadGuidance;
